import { omit } from 'lodash-es';
import type { IResponse } from '~/api/interface';

const DEFAULT_PAGESIZE = 25;

/**
 * 这里key是给useAsyncData使用，防止数据有缓存存在
 */
export enum SCROLL_LIST {
  SIMILAR_CLASS = 'similar_class',
  SIMILAR_TEMPLATE = 'similar_template',
  HOME_PRIME_PLAY = 'home_prime_play',
  HOME_PRIME_VIDEO = 'home_prime_video',
  SCENARIOS_MORE_TEMPLATE = 'scenarios_moreTemplate',
  TEMPLATE_LIST = 'template_list',
  HOME_LIST = 'home_list',
  INDUSTRY_LIST = 'industry_list',
  BEST_PRACTICES = 'best_practices',
  ADD_PROJECT = 'add_project',
  SITE_SEARCH = 'site_search',
  CHARACTER = 'character',
  CHARACTERS = 'characters', // todo: deprecated
}

interface ScrollOption {
  pageSize?: number | Ref<number>;
  key: string;
  server?: boolean;
}

type ScrollResType<
  T extends (
    ...args: any[]
  ) => Promise<IResponse<{ count?: number; results?: any[]; order_list?: any[] } | undefined>>,
> = T extends (...args: any[]) => Promise<
  IResponse<
    | {
        count?: number;
        results?: (infer M)[];
        order_list?: (infer M)[];
      }
    | undefined
  >
>
  ? M
  : never;
/**
 * 支持ssr
 * @param fn
 * @returns
 */
export function useScrollList<
  T extends (
    params: {
      page_size: number;
      page: number;
    },
    controller: { signal: AbortSignal },
  ) => Promise<
    IResponse<
      | {
          count?: number;
          results?: any[];
          order_list?: any[];
          [x: string]: any;
        }
      | undefined
    >
  >,
>(fn: T, option: ScrollOption) {
  const { pageSize: _pageSize, key, server } = option || {};

  type L = ScrollResType<T>;

  const pageSize = isRef(_pageSize) ? _pageSize : ref(_pageSize || DEFAULT_PAGESIZE);

  const pre = ref([]) as Ref<L[]>;

  const current = ref(1);
  let abortControl: AbortController | null = null;

  const {
    data,
    refresh: _refresh,
    execute: _execute,
    pending,
    status,
    clear: _clear,
  } = useAsyncData(
    key,
    async () => {
      abortControl = new AbortController();

      const res = await fn(
        {
          page_size: pageSize.value,
          page: current.value,
        },
        { signal: abortControl.signal },
      );

      abortControl = null;
      const { count: _count = 0, results = [], order_list = [] } = res?.data || {};

      const r = {
        ...res.data,
        count: _count,
        results: (results.length ? results : order_list).map(v =>
          omit(v as any, ['seo_description', 'seo_title', 'seo_keywords']),
        ) as L[],
      };
      return r as Omit<typeof res.data & typeof r, 'order_list'>;
    },
    { server, lazy: true },
  );

  let nextCallCount = 0;

  const list = computed<L[]>(() => {
    if (data.value) {
      return pre.value.concat(data.value.results);
    }
    return pre.value;
  });

  const total = computed(() => {
    return data.value?.count || 0;
  });

  async function next() {
    if (pending.value) {
      return;
    }

    const totalLength = pre.value.length + (data.value ? data.value.results.length : 0);

    if (totalLength >= total.value) {
      return;
    }
    const currentCallCount = nextCallCount;
    current.value += 1;
    const preResults = data.value?.results;
    await _execute();

    if (currentCallCount < nextCallCount) {
      // 在调用next过程中，调用了refresh，则next执行结果不采纳
      return;
    }

    if (preResults) {
      pre.value = pre.value.concat(preResults);
    }
  }

  function clear() {
    _clear();
    pre.value = [];
    current.value = 1;
  }

  /**
   * refresh时候，会重新拉数据，但是页面dom不会立刻消失，所以不要使用clear,而是在适当时候，清空历史数据
   * @returns
   */
  async function refresh() {
    nextCallCount++;
    current.value = 1;
    const res = await _refresh();
    pre.value = [];
    return res;
  }

  function abort() {
    abortControl?.abort();
  }

  return {
    list,
    recentlyData: data,
    pending,
    status,
    refresh,
    next,
    total,
    current,
    clear,
    abort,
  };
}
